import React from 'react';

import { MultiSelect, TableCell, TableHeaderCell } from '@hcs/design-system';
import {
  MlsStateGroup,
  PropertyStateFilterProps,
  TableCellProps,
  TableHeaderCellProps,
} from '@hcs/types';
import {
  PropertyStateArgsProps,
  PropertyStateFieldConfig,
  PropertyStateFields,
  PropertyStatePaths,
  PropertyStateTableCellProps,
  PropertyStateTableHeaderCellProps,
} from '@hcs/types';
import { formatListingStatusNormalized, formatMissing } from '@hcs/utils';

import { ListingStatusIndicator } from '../../../components/ListingStatusIndicator';

const FIELD = PropertyStateFields.currentStatusRental;
type FieldConfig = PropertyStateFieldConfig<typeof FIELD>;
const label = 'Rental Listing Status';
const labelShort = 'Rental Listing Status';
const propertyStatePath: PropertyStatePaths =
  '/complexFieldsRental/currentStatus';
const getValue: FieldConfig['getValue'] = (propertyStateArgs) => {
  const { propertyState, propertyStateType } = propertyStateArgs || {};
  if (propertyStateType === 'flat') {
    return propertyState?.[FIELD];
  }
  return propertyState?.complexFieldsRental?.currentStatus;
};
const formatValue: FieldConfig['formatValue'] = (propertyStateArgs) =>
  formatMissing(formatListingStatusNormalized(getValue(propertyStateArgs)));

// For displaying multiple fields in a single line
const labelMicro: FieldConfig['labelMicro'] = 'Status';
const formatValueShort: FieldConfig['formatValueShort'] = formatValue;

const formatInline: FieldConfig['formatInline'] = (propertyStateArgs) =>
  `${formatValue(propertyStateArgs)}`;
// Component for displaying a field from a schema
// eslint-disable-next-line react-refresh/only-export-components
const Display = ({ propertyStateArgs }: PropertyStateArgsProps) => {
  return <>{formatValue(propertyStateArgs)}</>;
};

// eslint-disable-next-line react-refresh/only-export-components
const HeaderCell = (props: PropertyStateTableHeaderCellProps) => {
  return <TableHeaderCell {...props}>{label}</TableHeaderCell>;
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultHeaderProps: Partial<TableHeaderCellProps> = {
  align: 'left',
  // Needed so child component type-checking passes
  isTableHeaderCell: true,
};
HeaderCell.defaultProps = defaultHeaderProps;

// eslint-disable-next-line react-refresh/only-export-components
const ContentCell = ({
  propertyStateArgs,
  ...tableCellProps
}: PropertyStateTableCellProps) => {
  const value = getValue(propertyStateArgs);
  return (
    <TableCell {...tableCellProps}>
      {value ? (
        <ListingStatusIndicator
          value={{
            type: 'listingStatus',
            value,
          }}
        />
      ) : (
        <Display propertyStateArgs={propertyStateArgs} />
      )}
    </TableCell>
  );
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultContentProps: Partial<TableCellProps> = {
  align: 'left',
  // Needed so child component type-checking passes
  isTableCell: true,
};
ContentCell.defaultProps = defaultContentProps;

const OPTIONS = [
  {
    label: 'Active',
    value: MlsStateGroup.Active,
  },
  {
    label: 'Contingent',
    value: MlsStateGroup.Contingent,
  },
  {
    label: 'Off Market',
    value: MlsStateGroup.OffMarket,
  },
  {
    label: 'Pending',
    value: MlsStateGroup.Pending,
  },
];

// eslint-disable-next-line react-refresh/only-export-components
const Filter = ({
  spatialFiltersInput,
  onChange,
  className,
  disabled,
}: PropertyStateFilterProps<typeof FIELD>) => {
  return (
    <MultiSelect
      className={className}
      disabled={disabled}
      options={OPTIONS}
      value={spatialFiltersInput.rentalMlsStateGroup || []}
      onChange={(value) => {
        onChange({
          field: FIELD,
          spatialFilterInputs: {
            rentalMlsStateGroup: value,
          },
        });
      }}
      dataHcName={`${FIELD}-filter`}
    />
  );
};

export const CURRENT_STATUS_RENTAL_CONFIG: FieldConfig = {
  field: FIELD,
  label,
  labelShort,
  propertyStatePath,
  getValue,
  labelMicro,
  formatValueShort,
  formatValue,
  formatInline,
  Filter,
  HeaderCell,
  ContentCell,
  Display,
};
